import type { TemplateId } from '../constants';
import { makeMembershipGuard } from '../utils/object';

export const OFFICIAL_ANALYTICS_PROVIDERS = ['ga', 'gtm'] as const;
export type OfficialAnalyticsProviderName = (typeof OFFICIAL_ANALYTICS_PROVIDERS)[number];
export const isOfficialAnalyticsProviderName = makeMembershipGuard(OFFICIAL_ANALYTICS_PROVIDERS);

export const ANALYTICS_EVENT_NAMES = {
  addToCart: 'add_to_cart',
  cartUpdate: 'cart_update',
  shoppingListCreate: 'shopping_list_create',
  shoppingListLoad: 'shopping_list_load',
  shoppingListUpdate: 'shopping_list_update',
  shoppingListDelete: 'shopping_list_delete',
  shoppingListExport: 'shopping_list_export',
  csvUpload: 'csv_upload',
  quickAdd: 'quick_add',
  finalStageCheckout: 'final_stage_checkout',
  shippingUpdate: 'add_shipping_info',
  beginCheckout: 'begin_checkout',
  purchase: 'purchase',
  viewCart: 'view_cart',
} as const;
export type AnalyticsEventName = keyof typeof ANALYTICS_EVENT_NAMES;
export const ANALYTICS_EVENT_NAME_KEYS = Object.keys(ANALYTICS_EVENT_NAMES) as AnalyticsEventName[];

export type AnalyticsProviderConfig = {
  handler: OfficialAnalyticsProviderName;
  events: Partial<Record<AnalyticsEventName, boolean>>;
};

export type AnalyticsOptions = {
  providers: AnalyticsProviderConfig[];
};

export const PAYMENT_METHOD_NAMES = [
  'paymentOnAccount',
  'paymentByInvoice',
  'upfrontPayment',
  'quote',
] as const;
export type PaymentMethodName = (typeof PAYMENT_METHOD_NAMES)[number];

export const STANDARD_CHECKOUT_ELEMENTS = [
  'customer-reference',
  'po-number',
  'shipping-requested-date',
] as const;
export type StandardCheckoutElement = (typeof STANDARD_CHECKOUT_ELEMENTS)[number];

export const isStandardCheckoutElementId = makeMembershipGuard(STANDARD_CHECKOUT_ELEMENTS);

export const ACCOUNT_SECTION_IDS = [
  'spark-slot-my-account',
  'recent-activity',
  'shopping-lists',
  'financials',
  'my-details',
  'reports',
  'address-book',
  'company-users',
] as const;
export type AccountSectionId = (typeof ACCOUNT_SECTION_IDS)[number];

export type AccountSectionConfiguration = {
  order?: AccountSectionId[];
  hide?: AccountSectionId[];
};

type SectionConfiguration = {
  'my-account'?: AccountSectionConfiguration;
};

export type DisplayOptions = {
  darkTheme: boolean;
  savingsUseRrp: boolean;
  showRrpToggle: boolean;
  showPriceBreakSavings: boolean;
  roundPriceBreakPercentages: boolean;
  hideFinancials?: boolean;
  orderListShowShippingAddress: boolean;
  sectionConfiguration: SectionConfiguration;
  showSlots: TemplateId[];
};

export type LocaleEntry = { id: number; key: string; value: string };

export type LocaleGroup = { id: number; locale: string; entries: LocaleEntry[] };

export type CheckoutElementTranslations = LocaleGroup[];

export type CheckoutDropdownOptionItem = {
  value: string;
  translations: LocaleGroup[];
};

export type CheckoutDropdownGroup = {
  translations?: LocaleGroup[];
  items: CheckoutDropdownOptionItem[];
};

export type CustomCheckoutElementAttributes = {
  type?: string;
  value?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  maxlength?: string;
  minlength?: string;
  min?: string;
  max?: string;
  step?: string;
  pattern?: string;
  maxsize?: string;
  fileextensions?: string[];
};

export const CHECKOUT_STRING_ATTRIBUTES = [
  'type',
  'value',
  'placeholder',
  'maxlength',
  'minlength',
  'min',
  'max',
  'step',
  'pattern',
  'maxsize',
] as const satisfies readonly (keyof CustomCheckoutElementAttributes)[];
export type CheckoutStringAttribute = (typeof CHECKOUT_STRING_ATTRIBUTES)[number];

/** Boolean-valued custom checkout attributes; the single source for parsing and serialization. */
export const CHECKOUT_BOOLEAN_ATTRIBUTES = [
  'required',
  'disabled',
  'hidden',
] as const satisfies readonly (keyof CustomCheckoutElementAttributes)[];

type StandardCheckoutElementConfig = {
  id: StandardCheckoutElement;
  required?: boolean;
  hidden?: boolean;
  disabled?: boolean;
};

type CustomCheckoutElementConfig = {
  name: string;
  translations: CheckoutElementTranslations;
  attributes: CustomCheckoutElementAttributes;
  options?: CheckoutDropdownGroup[];
};

export type CheckoutElement =
  | ({ kind: 'standard' } & StandardCheckoutElementConfig)
  | ({ kind: 'custom' } & CustomCheckoutElementConfig);

export type SparkOptions = {
  display: DisplayOptions;
  translations: LocaleGroup[];
  showTranslations: boolean;
  paymentMethodsOrder: PaymentMethodName[];
  saveToAddressBookDefault: boolean;
  checkoutCustomElements: CheckoutElement[];
  analytics: AnalyticsOptions | null;
};

export const EDITABLE_OPTION_KEYS = [
  'display',
  'showTranslations',
  'translations',
  'paymentMethodsOrder',
  'saveToAddressBookDefault',
  'checkoutCustomElements',
  'analytics',
] as const satisfies (keyof SparkOptions)[];
